<template>
  <div class="schedule schedule--padded">

    <!-- Component to display the top bar of the schedule and the selected events if any -->
    <FiltersZone
      class="schedule__drawer"
      no-options-button
      :force-open="openSidePanel"
      custom-modal-class="schedule__drawer__modal"
      @close="unselectAllEvents"
    >
      <div slot="shortcuts" class="fw-bold">
        {{ settings.dayStart.format("MMMM YYYY") }}
      </div>
      <div slot="buttons">
        <div class="schedule__date-selector h-v-centerer generic-border generic-border--small-text-btn">
          <div class="schedule__date-selector__arrow generic-border--small-text-btn h-v-centerer" @click="prevDate">
            <Icon>{{ arrowDownLine }}</Icon>
          </div>
          <div class="schedule__date-selector__current generic-border--small-text-btn" @click="editDate">
            {{ currentRange }}
            <input ref="dpEdit" class="chart-period-selector__dp-field">
          </div>
          <div class="schedule__date-selector__arrow generic-border--small-text-btn h-v-centerer" @click="nextDate">
            <Icon>{{ arrowDownLine }}</Icon>
          </div>
        </div>
      </div>

      <!-- Displayed when an element on the schedule is clicked -->
      <div class="schedule__drawer__content">
        <div class="schedule__drawer__content__wrapper">
          <div class="schedule__drawer__title v-centerer f-w">
            <Icon v-if="selectedEvent" rotate-right @click.native="backFromSelectedEvent">{{ arrowDownLine }}</Icon>
            <h2 class="basic-sub-title">
              {{ selectedEvent ? this.$t('message.EVENT') : this.$t('message.EVENTS') }}
            </h2>
          </div>
          <div class="filters-zone__modal__sep " />
          <div v-if="selectedEvent">
            <h3>
              {{ selectedEvent.date.format(this.$t('llFullDateFormat')) }}
            </h3>
            <ScheduleEvent
              :event="selectedEvent"
              :hours-height="hoursHeight"
              :day-start="settings.dayStart"
            />
            <div v-if="selectedEvent.type === 1" class="filters-zone__modal__sep " />
            <MeetingPanelContent v-if="selectedEvent.type === 1" :meeting="selectedEvent" />
          </div>
          <div v-else-if="selectedEvents">
            <h3>
              {{ selectedEvents[0].date.format(this.$t('llFullDateFormat')) }}
            </h3>
            <ScheduleEvent
              v-for="(event, evtIndex) in selectedEvents"
              :key="`${evtIndex}-${keyModifier}`"
              class="mt10em"
              :event="event"
              :day-start="settings.dayStart"
              @click.native.stop="selectEvent(event)"
            />
          </div>
        </div>
        <div class="schedule__drawer__bottom">
          <div v-if="this.selectedEventPlanningError" class="field-hint">
            {{ $t('message.SCHEDULE_PLANNING_ERROR') }}
          </div>
          <button
            v-if="selectedEvent"
            class="schedule__drawer__bottom__button generic-border generic-border--text-btn generic-border--blue"
            :class="{ 'generic-border--disabled': this.selectedEventPlanningError }"
            @click="startButtonClicked"
          >
            {{ $t("message.START_NOW") }}
          </button>
        </div>
      </div>
    </FiltersZone>

    <div class="schedule__sep" />

    <!-- Schedule content as a calendar view -->
    <div class="f-h-below-filters schedule__wrapper">
      <div class="f-h schedule__wrapper__hours">
        <div
          v-for="(hour, hIndex) in hoursLines"
          :key="hIndex"
          class="schedule__wrapper__hours__hour"
          :style="hour.style"
        >
          <div class="text">
            {{ hour.title }}
          </div>
        </div>
      </div>
      <div class="f-h f-w schedule__wrapper__items">
        <div
          v-for="(day, index) in compDays"
          :key="index"
          class="schedule__wrapper__items__column f-h"
          :style="dayColStyle"
        >
          <div class="schedule__wrapper__items__column__head" @click.stop="displayDayEvents(day)">
            {{ day.date.format('dddd DD') }}
          </div>
          <div ref="dayCol" class="schedule__wrapper__items__column__events">
            <ScheduleEvent
              v-for="(event, evtIndex) in day.events"
              :key="`${evtIndex}-${keyModifier}`"
              :ref="index + '-' + evtIndex"
              :event="event"
              @click.native.stop="selectEvent(event)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Schedule page.
 * Displays a list of events planned week by week in a calendar-like view.
 */

import $ from 'jquery';
import toastr from 'toastr';
import { cloneDeep, isEqual } from 'lodash';
import { FETCH_TEAMS } from '@/store/action-types';
import { START_MEETING } from '@/store/modules/meeting/action-types';
// Import components
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import MeetingPanelContent from '@/components/meeting-panel-content.vue';
import ScheduleEvent from '@/components/schedule-event.vue';
// Utils and assets
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { ScheduleEvt, scheduleEventColors } from '@/utils/classes/ScheduleEvt';
import { arrowDownLine, arrowDownThin } from '@/assets/icons/';

const initialDaysValue = [
  { date: null, events: [] },
  { date: null, events: [] },
  { date: null, events: [] },
  { date: null, events: [] },
  { date: null, events: [] },
  { date: null, events: [] },
  { date: null, events: [] },
];

export default {

  name: 'Schedule',

  components: {
    FiltersZone,
    Icon,
    MeetingPanelContent,
    ScheduleEvent,
  },

  data() {
    return {
      START_MEETING,
      arrowDownLine,
      arrowDownThin,
      openSidePanel: false,
      days: initialDaysValue,
      allEvents: [],
      settings: { dayStart: this.$moment('07:00', 'HH:mm'), dayEnd: this.$moment('20:00', 'HH:mm') },
      /** Whether we should display events as a list or positioned calendar items. 0 : list */
      keyModifier: 1,
      mounted: false,
      resizeEvent: 1,
      // Clicked on a single event
      selectedEvent: null,
      selectedEventPlanningError: false,
      // Clicked on a group of events
      selectedEvents: null,
      selectedDate: null,
      eventTypes: ['?', this.$t('message.MEETING'), this.$t('message.GW')],
      datepickerEndDate: null,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.SCHEDULE'),
    };
  },

  computed: {
    isSmartphonePortrait() {
      return this.$store.state.device.isSmartphonePortrait;
    },
    /** Moment: Week's start date */
    startDate() {
      return this.$moment(this.endDate).startOf(this.isSmartphonePortrait ? 'day' : 'isoWeek');
    },
    /** Moment: Week's end date */
    endDate: {
      get() {
        const { scheduleEndDate } = this.$route.query;
        const defaultDate = this.isSmartphonePortrait ? this.$moment() : this.$moment().endOf('isoWeek');
        return scheduleEndDate ? this.$moment(scheduleEndDate) : defaultDate;
      },
      set(mDate) {
        const query = Object.assign({}, { ...this.$route.query, scheduleEndDate: mDate.format('YYYY-MM-DD') });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Moment: start date of the dynamic date picker */
    datepickerStartDate() {
      return this.$moment(this.datepickerEndDate).startOf('isoWeek');
    },
    /** String: Displayed range at the top right */
    currentRange() {
      let df = this.$t('fullDateFormat');
      if (this.isSmartphonePortrait) {
        df = this.$t('yyDateFormat');
      }
      if (this.startDate.isSame(this.endDate, 'day')) {
        return this.startDate.format(df);
      }
      return `${this.startDate.format(df)} - ${this.endDate.format(df)}`;
    },
    /** Number: Height in pixels of a schedule event item */
    tileHeight() {
      return 4 * this.$store.getters.fontSize;
    },
    /** Array: working days of the current team */
    workingDays() {
      return this.currentTeam.workingDays || [true, true, true, true, true, true, true];
    },
    /** Array: One gray line per hour in the background */
    hoursLines() {
      const hours = [];
      const ds = this.settings.dayStart;
      const de = this.settings.dayEnd;
      if (de.isAfter(ds)) {
        const iHour = this.$moment(ds).startOf('hour');
        if (iHour.diff(ds, 's', true) === 0) {
          hours.push({
            title: iHour.format('HH:mm'),
            style: { top: '0px' },
          });
        }
        for (let i = 0; i < this.nHours; i++) {
          iHour.add(1, 'hour');
          hours.push({
            title: iHour.format('HH:mm'),
            style: {
              top: `${i * this.hoursHeight + this.firstHourMargin}px`,
            },
          });
        }
      }
      return hours;
    },
    /** Number: Top margin of the first hour line */
    firstHourMargin() {
      const ds = this.settings.dayStart;
      const eoh = this.$moment(ds).endOf('hour');
      const nHours = eoh.diff(ds, 'hours', true);
      return nHours * this.hoursHeight;
    },
    /** Number: Height in pixels of an events container */
    containerHeight() {
      return this.mounted && this.$refs.dayCol ? this.$refs.dayCol[0].offsetHeight : 0;
    },
    /** Number: The height of an hour time in the schedule */
    hoursHeight() {
      return this.containerHeight / this.nHours;
    },
    /** Number: The number of hours in the schedule */
    nHours() {
      const ds = this.settings.dayStart;
      const de = this.settings.dayEnd;
      if (de.isAfter(ds)) {
        const nHours = de.diff(ds, 'hours', true);
        return nHours;
      }
      return 0;
    },
    dayColStyle() {
      let nDays = 0;
      const max = this.isSmartphonePortrait ? 1 : 7;
      for (let i = 0; i < max; i++) {
        if (this.workingDays[i]) {
          nDays++;
        }
      }
      const perc = Math.floor((100 / nDays) * 10000) / 10000;
      return {
        width: `calc(${perc}% - 10px)`,
      };
    },
    /** Title of the focused event */
    selectedEventTitle() {
      if (this.selectedEvent != null) {
        return `${this.selectedEvent.name} (${this.eventTypes[this.selectedEvent.type]})`;
      }
      return '';
    },
    /** Array: Array of array of events (1 array for each day) */
    daysWithEvents() {
      const days = cloneDeep(this.demoMode ? this.demoDays : this.daysFromEvents);
      const ds = this.settings.dayStart;
      days.forEach((day) => {
        day.events.forEach((dayEvent, evtIdx) => {
          dayEvent.absolute = true;
          // Top position of the element if there's no overlap
          const top = this.$moment(dayEvent.time, 'HH:mm').diff(ds, 'hours', true) * this.hoursHeight;
          const overlappingElts = day.events.filter((e, eIdx) => eIdx < evtIdx && e.bottomPosition > top);
          if (overlappingElts.length === 0) {
            // No overlapping element, position the event properly
            dayEvent.topPosition = top;
            dayEvent.bottomPosition = top + this.tileHeight;
          } else if (overlappingElts.length === 1) {
            // 1 overlapping element, position the element vertically and specify half width
            dayEvent.topPosition = top;
            dayEvent.bottomPosition = top + this.tileHeight;
            const isRight = !overlappingElts[0].halfWidth || overlappingElts[0].halfWidth.left;
            dayEvent.halfWidth = { right: isRight, left: !isRight };
            overlappingElts[0].halfWidth = { left: isRight, right: !isRight };
          } else {
            // More elements overlapping, group the elements
            dayEvent.bottomPosition = top + this.tileHeight;
            overlappingElts.push(dayEvent);
            overlappingElts[0].groupedEvents = [];
            overlappingElts.forEach((elt, idx) => {
              overlappingElts[0].groupedEvents.push(elt);
              overlappingElts[0].bottomPosition = Math.max(overlappingElts[0].bottomPosition, elt.bottomPosition);
              overlappingElts[idx].halfWidth = null;
              overlappingElts[idx].inGroup = idx > 0 ? overlappingElts[0].id : 0;
            });
          }
        });
      });
      // Set colors
      days.forEach(({ events }) => {
        events.forEach((evt, i) => {
          evt.color = scheduleEventColors[i % scheduleEventColors.length];
        });
      });

      return days;
    },
    /**
     * Create an array of days of the current week.
     * Each element of the array will be an Object just like initialDaysValue.
     * @return {Array}  Array like initialDaysValue
     */
    daysFromEvents() {
      const days = cloneDeep(this.isSmartphonePortrait ? [initialDaysValue[0]] : initialDaysValue);
      const firstDay = this.$moment(this.startDate);
      this.allEvents.forEach((evt) => {
        switch (Number(evt.repeatType)) {
          case 0: {
            days.forEach((day, j) => {
              const idx = ((j + firstDay.isoWeekday() - 1) % 7 + 7) % 7;
              const rep = evt.repeatData[idx];
              if (rep.on) {
                evt = Object.assign({}, evt);
                evt.time = rep.time;
                day.events.push(new ScheduleEvt(evt));
              }
            });
            break;
          }
          case 1: {
            const wday = Number(evt.repeatData.day).sBetweenBoundaries(0, 6);
            evt.time = evt.repeatData.time;
            const idx = ((wday - (firstDay.isoWeekday() - 1)) % 7 + 7) % 7;
            if (days[idx]) {
              days[idx].events.push(new ScheduleEvt(evt));
            }
            break;
          }
          case 2: {
            const dayIdx = Number(evt.repeatData.day) - this.$moment(firstDay).date();
            evt.time = evt.repeatData.time;
            if (days[dayIdx]) {
              days[dayIdx].events.push(new ScheduleEvt(evt));
            }
            break;
          }
          case 3: {
            const d = this.$moment(`${evt.repeatData.day} ${evt.repeatData.time}`);
            if (d.isValid() && d.isAfter(this.startDate) && d.isBefore(this.endDate)) {
              const day = d.date() - firstDay.date();
              evt.time = d.format('HH:mm');
              days[day].events.push(new ScheduleEvt(evt));
            }
            break;
          }
          default:
        }
      });

      days.forEach((day, dayIdx) => {
        day.date = this.$moment(firstDay).add(dayIdx, 'd');
        day.events.forEach((event) => {
          event.date = day.date;
        });
        day.events.sort((a, b) => (this.$moment(a.time, 'HH:mm').isAfter(this.$moment(b.time, 'HH:mm')) ? 1 : -1));
      });

      return days;
    },
    compDays() {
      return this.daysWithEvents.filter((d, idx) => !!this.workingDays[idx]);
    },
  },

  created() {
    // Update teams and their users for NextMeetingPanel components
    this.$store.dispatch(FETCH_TEAMS);
    this.refresh();
    $(window).on(this.resizeEventName, () => {
      this.mounted = false;
      this.$nextTick(() => {
        this.mounted = true;
      });
    });
  },

  mounted() {
    this.mounted = true;
  },

  beforeDestroy() {
    $('.ui-datepicker-calendar .shizen-datepicker__day').off();
  },

  methods: {
    prevDate() {
      this.endDate = this.$moment(this.endDate).subtract(1, this.isSmartphonePortrait ? 'day' : 'week');
    },
    nextDate() {
      this.endDate = this.$moment(this.endDate).add(1, this.isSmartphonePortrait ? 'day' : 'week');
    },
    /**
     * Trigger the date picker to update the range.
     */
    editDate() {
      this.datepickerEndDate = this.$moment(this.endDate);
      const $inputEl = $(this.$refs.dpEdit);
      $inputEl.val(this.datepickerEndDate.format('YYYY-MM-DD'));
      $inputEl.datepicker('destroy');
      $inputEl.datepicker({
        classes: 'shizen-datepicker chart-period-selector-dp',
        changeMonth: true,
        changeYear: true,
        showButtonPanel: true,
        showOtherMonths: true,
        selectOtherMonths: true,
        nextText: this.$t('message.NEX'),
        prevText: this.$t('message.PREV'),
        currentText: this.$t('message.TODAY_SHORT'),
        closeText: this.$t('message.DONE'),
        dateFormat: 'yy-mm-dd',
        defaultDate: this.datepickerEndDate.format('YYYY-MM-DD'),
        beforeShowDay: (date) => {
          // Set a custom class to values in the range to display the range properly
          let customClass = '';
          if (this.datepickerStartDate.isSame(date, 'd') || this.datepickerEndDate.isSame(date, 'd')) {
            customClass = 'ui-datepicker-currentrange';
            if (this.datepickerStartDate.isSame(date, 'd')) {
              customClass += ' ui-datepicker-currentrange--start';
            } else {
              customClass += ' ui-datepicker-currentrange--end';
            }
          }
          if (this.datepickerStartDate.isSameOrBefore(date, 'd') && this.datepickerEndDate.isSameOrAfter(date, 'd')) {
            customClass += ' ui-datepicker-inrange';
          }
          customClass += ` shizen-datepicker__day ${this.$moment(date).format('YYYY-MM-DD')}`;
          return [true, customClass, null];
        },
        beforeShow: (input, inst) => {
          inst.dpDiv.addClass('chart-period-selector__date-picker');
        },
        onClose: (input, inst) => {
          inst.dpDiv.removeClass('chart-period-selector__date-picker');
        },
        onSelect: (dateText, inst) => {
          this.endDate = this.datepickerEndDate;
        },
        onChangeMonthYear: () => {
          this.$nextTick(this.watchDatepickerHover);
        },
      });
      $inputEl.datepicker('show');
      this.watchDatepickerHover();
    },
    /** Liste to hovering events on datepicker days to visually update it */
    watchDatepickerHover() {
      $('.ui-datepicker-calendar .shizen-datepicker__day').on('mouseenter', (e) => {
        if (e && e.currentTarget && e.currentTarget.className) {
          const dateClass = e.currentTarget.className
            .split(' ')
            .find(cssClass => cssClass.match(/\d{4}-\d{2}-\d{2}/));
          if (dateClass) {
            this.datepickerEndDate = this.$moment(dateClass).endOf('isoWeek');
            // Wait nextTick for datepickerStartDate to update
            this.$nextTick(this.updateCalendarFocusedDates);
          }
        }
      });
    },
    /** When a datepicker day is hovered, change the display to illustrate how the range would look like */
    updateCalendarFocusedDates() {
      $('.ui-datepicker-calendar .shizen-datepicker__day').each((idx, elt) => {
        if (elt && elt.className) {
          const dateClass = elt.className.split(' ').find(cssClass => cssClass.match(/\d{4}-\d{2}-\d{2}/));
          if (dateClass) {
            const md = this.$moment(dateClass);
            let customClass = '';
            if (this.datepickerStartDate.isSame(md, 'd') || this.datepickerEndDate.isSame(md, 'd')) {
              customClass = 'ui-datepicker-currentrange';
              if (this.datepickerStartDate.isSame(md, 'd')) {
                customClass += ' ui-datepicker-currentrange--start';
              } else {
                customClass += ' ui-datepicker-currentrange--end';
              }
            }
            if (this.datepickerStartDate.isSameOrBefore(md, 'd') && this.datepickerEndDate.isSameOrAfter(md, 'd')) {
              customClass += ' ui-datepicker-inrange';
            }
            $(elt).removeClass('ui-datepicker-currentrange ui-datepicker-inrange');
            $(elt).removeClass('ui-datepicker-currentrange--start ui-datepicker-currentrange--end');
            $(elt).addClass(customClass);
          }
        }
      });
    },
    startButtonClicked() {
      if (this.selectedEventPlanningError) {
        return;
      }
      if (this.selectedEvent.type === 1) {
        // Meeting
        this.startMeeting(this.selectedEvent);
      } else if (this.selectedEvent.type === 2) {
        // GW
        this.$router.push(`/gw-start/${this.selectedEvent.gw}`);
      }
    },
    startMeeting(meeting) {
      // Removing Moment object since the dispatch doesn't work with it
      meeting.day = undefined;
      this.$store.dispatch(`meeting/${START_MEETING}`, { meeting });
    },
    refresh() {
      this.$http.post('/server/teams/getEvents.php', {}).then(({ data }) => {
        if (typeof data === 'object') {
          this.settings = data.settings || {};
          this.settings.dayStart = this.$moment(this.settings.dayStart, 'HH:mm:ss');
          this.settings.dayEnd = this.$moment(this.settings.dayEnd, 'HH:mm:ss');
          if (this.settings.dayStart.isAfter(this.settings.dayEnd)) {
            this.settings.dayStart = this.settings.dayStart.startOf('day');
            this.settings.dayEnd = this.settings.dayEnd.endOf('day');
          }
          this.allEvents = data.plannings || [];
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
      $(window).on(`resizeEnd.${this.uuid}`, () => {
        this.keyModifier += this.allEvents.length;
      });
    },
    /** Show all the events of a day */
    displayDayEvents(date) {
      if (this.selectedEvent) {
        this.selectedEvent = null;
      }
      this.selectedEvents = cloneDeep(date.events).map((evt) => {
        evt.groupedEvents = [];
        evt.inGroup = 0;
        return { ...evt, absolute: undefined };
      });
      this.openSidePanel = true;
    },
    /** Show the selected event(s) */
    selectEvent(evt) {
      if (evt.groupedEvents.length > 0) {
        this.selectedEvent = null;
        // Remove the groupedEvents from the first item
        const events = cloneDeep(evt.groupedEvents);
        events[0].groupedEvents = [];
        this.selectedEvents = events.map(e => ({ ...e, absolute: undefined }));
      } else {
        this.selectedEvent = { ...evt, absolute: undefined };
        if (this.selectedEvent.type === 1) {
          this.fetchData();
        }
      }
      this.openSidePanel = true;
    },
    /** Clicked the back button in the selected event panel */
    backFromSelectedEvent() {
      this.selectedEvent = null;
      if (!this.selectedEvents) {
        this.openSidePanel = false;
      }
    },
    unselectAllEvents() {
      this.selectedEvent = null;
      this.selectedEvents = null;
      this.openSidePanel = false;
      this.selectedEventPlanningError = false;
    },
    /** Fetch selected event data */
    fetchData() {
      if (this.demoMode) {
        return;
      }
      const evt = this.selectedEvent;
      this.$http.post('/server/meetings/get.php', { next: true, planningId: evt.id }).then(({ data }) => {
        if (data === -1) {
          toastr.error(this.$t('message.SCHEDULE_PLANNING_ERROR'));
          this.selectedEventPlanningError = true;
        } else if (typeof data === 'object' && data.id === evt.id) {
          this.selectedEvent = new ScheduleEvt({ ...evt, ...data, date: this.$moment(evt.date) });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    createHelpSteps() {
      $('.left-drawer-panel').addClass('hidden');
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.SCHEDULE_INTRO')),
        new IAHelpStep('.schedule-event', this.$t('help.SCHEDULE_ITEMS'), (e, next) => {
          e.stopPropagation();
          this.selectEvent(this.compDays[0].events[0]);
          setTimeout(next, 300);
          return true;
        }),
        new IAHelpStep('.schedule__drawer__content', this.$t('help.SCHEDULE_CLICK_ITEM'), (() => {
          this.unselectAllEvents();
        })),
        new IAHelpStep('.schedule-event--group', this.$t('help.SCHEDULE_CALENDAR')),
      ];
    },
    helpHidden() {
      this.keyModifier += this.allEvents.length;
    },
    makeDemoValues() {
      this.demoDays = [];
      const monday = this.$moment(this.startDate).startOf('isoWeek');
      for (let j = 0; j < 7; j++) {
        this.demoDays.push({ events: [] });
        this.demoDays[j].date = this.$moment(monday);
        monday.add(1, 'd');
        this.demoDays[j].events.push(new ScheduleEvt({
          id: '2',
          name: this.$t('help.DEMO_EVENT1'),
          time: '08:00',
          type: 1,
          duration: 1080,
          color: scheduleEventColors[0],
          steps: [],
          date: this.$moment(monday),
        }));
        if (j === 0) {
          this.demoDays[j].events.push(new ScheduleEvt({
            id: '2',
            name: this.$t('help.DEMO_EVENT2'),
            time: '10:30',
            type: 1,
            duration: 1080,
            color: scheduleEventColors[1],
            date: this.$moment(monday),
          }));
        } else if (j === 1) {
          const defaultEvt = {
            id: 3,
            name: this.$t('help.DEMO_EVENT2'),
            time: '11:00',
            type: 1,
            duration: 1080,
            color: scheduleEventColors[1],
            date: this.$moment(monday),
          };
          this.demoDays[j].events.push(new ScheduleEvt(defaultEvt));
          this.demoDays[j].events.push(new ScheduleEvt({ ...defaultEvt, id: 4 }));
          this.demoDays[j].events.push(new ScheduleEvt({ ...defaultEvt, id: 5 }));
        }
        this.demoDays[j].events.push(new ScheduleEvt({
          id: '2',
          name: this.$t('help.DEMO_EVENT3'),
          time: '14:00',
          type: 2,
          duration: 1080,
          color: scheduleEventColors[2],
          date: this.$moment(monday),
        }));
      }
      this.keyModifier += this.allEvents.length;
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vars.scss';

.schedule {
  --schedule-event-height: 4rem;

  &--padded {
    margin: 1rem;
    width: calc(100% - 2rem);
    height: calc(100% - 2rem);
    background: white;
    border-radius: $main-border-radius;
    position: relative;
    overflow: hidden;
  }

  &__wrapper {
    display: flex;

    &__hours {
      margin-top: 3.3rem;
      position: relative;

      &__hour {
        width: 50px;
        position: absolute;
        left: 0px;
        text-align: center;
      }
    }

    &__items {
      margin-left: 50px;

      &__column {
        display: inline-block;
        width: calc(14.28% - 10px);/* 100 / 7 - margins */
        vertical-align: top;
        margin: 0px 5px;

        &__head {
          margin: .4rem 0;
          font-size: 1em;
          font-weight: 500;
          line-height: 1.3rem;
          text-align: center;
          padding: .6rem;
          cursor: pointer;
          border-radius: $high-border-radius;

          &:hover {
            background: $color-body-background;
          }
        }

        &__events {
          height: calc(100% - 3.3rem);
          position: relative;
        }
      }
    }
  }

  &__date-selector {
    margin: 0px 16px;
    position: relative;

    &.generic-border--small-text-btn {
      padding: 0;

      &:hover {
        background: $color-gray-background;
      }

      &:active {
        box-shadow: none;
      }
    }

    > .generic-border--small-text-btn {
      background: transparent;
    }

    &__current {
      justify-content: center;
      white-space: nowrap;
      cursor: pointer;

      &.generic-border--small-text-btn {
        padding: 0 0.5rem;
      }
    }

    &__arrow {
      width: $small-button-height;
      height: $small-button-height;

      &.generic-border--small-text-btn {
        padding: 0;
      }

      .icon {
        margin: auto;
        width: .8rem;
        height: .8rem;
      }

      &:first-child .icon {
        transform: rotate(90deg);
      }

      &:last-child .icon {
        transform: rotate(270deg);
      }
    }

    .generic-border--icon-button:first-child .icon {
      transform: rotate(90deg);
    }

    .generic-border--icon-button:last-child .icon {
      transform: rotate(270deg);
    }

    &--disabled {
      opacity: 0.15;
      cursor: default !important;
    }
  }

  &__sep {
    background: $color-shizen-gray-lighter;
    height: 1px;
    width: 100%;
  }

  &__drawer {

    &__modal {
      &.filters-zone__modal.slide-fade-modal .slide-fade-modal__content {
        padding: 0px;

        .filters-zone__content {
          height: 100%;
        }
      }

      .schedule__drawer__content {
        display: flex;
        flex-flow: column;
        height: 100%;

        &__wrapper {
          flex: 1;
          overflow: auto;
          min-height: 0px;
          padding: 0px 1.4rem;
        }

        .schedule__drawer__title {
          margin-top: 1.5rem;

          & > h2.basic-sub-title {
            margin-top: 0;
            padding: 0;

            &:after {
              display: none;
            }
          }

          .icon {
            width: 0.9rem;
            height: 0.9rem;
            padding: .5rem;
            border-radius: $medium-border-radius;
            margin-right: .7rem;
            cursor: pointer;

            &:hover {
              background: $color-gray-background-hover;
            }
          }
        }
      }
    }

    &__back {
      transform: rotate(90deg);
    }

    &__bottom {
      display: flex;
      justify-content: flex-end;
      margin: 1.5rem;
      flex: 0 0 auto;
    }
  }

  .schedule-event {
    height: var(--schedule-event-height);
  }
}

@media (max-width: $smartphone-landscape) {
  .schedule {

    &--padded {
      margin: .5rem;
      width: calc(100% - 1rem);
      height: calc(100% - 1rem);
    }

    &__wrapper {
      &__hours {
        margin-top: 2.3rem;

        &__hour {
          width: 30px;
          font-size: 0.9rem;
        }
      }

      &__items {
        margin-left: 30px;

        &__column {
          &__head {
            margin: 0.2rem 0;
            padding: 0.3rem;
          }
        }
      }
    }
  }
}

</style>
